import { DownOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Row, Typography } from 'antd';
import { useSession } from 'next-auth/react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCurrentTicket from '../../hooks/ticketDetailPage/useCurrentTicket';
import useCurrentTicketKey from '../../hooks/ticketDetailPage/useCurrentTicketKey';
import useCurrentTicketStatus from '../../hooks/ticketDetailPage/useCurrentTicketStatus';
import { statusOptions } from '../../model/types';
import { deleteTicket } from '../../services/tickets/tickets';
import { useTabStore } from '../../store/RootStoreContext';
import { isAdmin } from '../../utils/accountHelper';
import requestWithMessage from '../../utils/requestWithMessage';
import { redirectTo } from '../../utils/routeHelper';
import TicketMergeModal, { TicketMergeModalRef } from './TicketMergeModal';
const { Text } = Typography;

enum TicketActionMap {
    delete = 'delete',
    merge = 'merge'
}

export default function TicketActionButton() {
    const { t } = useTranslation('common');
    const { t: tTickets } = useTranslation('tickets');
    const {
        data: {
            userInfo: { rls }
        }
    } = useSession();

    const modalRef = useRef<TicketMergeModalRef>(null);
    const [isOpen, setIsOpen] = useState(false);
    const { data: ticket } = useCurrentTicket();
    const ticketStatus = useCurrentTicketStatus();
    const ticketKey = useCurrentTicketKey();
    const { closeTabAndGetNewTargetUrl } = useTabStore();
    const [modal, contextHolder] = Modal.useModal();

    const isClosedTicket = ticketStatus === statusOptions.Closed;
    const mergeDisabled = !ticketStatus || isClosedTicket;

    const handleMenuClick: MenuProps['onClick'] = async (info) => {
        try {
            if (info.key === TicketActionMap.merge) {
                setIsOpen(false);
                modalRef.current.open();
                return;
            }
            if (info.key === TicketActionMap.delete) {
                setIsOpen(false);
                showDeleteTicketConfirm();
            }
        } catch (error) {
            // console.error('TicketActionButton handleMenuClick, menuInfo:', info, 'error', error);
        }
    };
    const showDeleteTicketConfirm = () => {
        modal.confirm({
            title: t('ticketAction.title'),
            icon: <ExclamationCircleOutlined />,
            content: <Text strong>{t('ticketAction.deletingInfo')}</Text>,
            onOk() {
                return deleteTicketAction();
            },
            okText: t('delete')
        });
    };
    const deleteTicketAction = async () => {
        try {
            const startMessage = t('ticketAction.message.onDeleteStart');
            const finishMessage = t('ticketAction.message.onDeleteFinish');

            await requestWithMessage({
                key: 'deleteTicket',
                startMessage,
                finishMessage,
                apiRequest: async () => {
                    await deleteTicket(ticketKey);
                    setIsOpen(false);
                    const targetUrl = closeTabAndGetNewTargetUrl(ticketKey);
                    redirectTo(targetUrl);
                }
            });
        } catch (error) {
            // console.error('TicketActionButton deleteTicketAction', 'error', error);
        }
    };
    const items: MenuProps['items'] = [
        { label: tTickets('associate.actionButton'), key: TicketActionMap.merge },
        {
            label: t('delete'),
            key: TicketActionMap.delete
        }
    ]
        .filter(({ key }) => !(key === TicketActionMap.merge && mergeDisabled))
        .filter(({ key }) => !(key === TicketActionMap.delete && !isAdmin(rls[0])));

    const menuProps = {
        items,
        onClick: handleMenuClick
    };

    return (
        <Row justify="center">
            {items.length > 0 && (
                <Dropdown
                    menu={menuProps}
                    onOpenChange={(flag) => setIsOpen(flag)}
                    open={isOpen}
                    placement="bottomRight"
                    trigger={['click']}
                >
                    <Button icon={<DownOutlined style={{ fontSize: 12 }} />} />
                </Dropdown>
            )}
            <TicketMergeModal ticket={ticket} ref={modalRef} />
            {contextHolder}
        </Row>
    );
}
